import * as React from 'react';
import {memo, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from 'react-redux';
import ScrollArea from 'react-scrollbar';
import {Accordion, Card, useAccordionButton} from "react-bootstrap";
import {Col, Label, Row} from 'reactstrap'
import {getLogs, setLogs, toggleQuickPanel} from './store/logSlice';
import reducer from './store';
import withReducer from "../../store/withReducer";
import InitialsAvatar from "react-initials-avatar";
import './styles.scss';


function CustomToggle({eventKey}) {
    const [isOpen, setIsOpen] = useState(false);
    const decoratedOnClick = useAccordionButton(eventKey, () => setIsOpen(v => !v));
    return (
        <button type="button" className="btn btn-primary-outline pull-right" onClick={decoratedOnClick}>
            <i className={`fa ${isOpen ? 'fa-chevron-circle-up' : 'fa-chevron-circle-down'}`}/>
        </button>
    );
}

function TagPills({tags, color}) {
    if (!tags || tags.length === 0) return null;
    const isAdd = color === 'success';
    return (
        <div style={{display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5}}>
            {tags.map((tag, i) => (
                <span key={i} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 10px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 500,
                    background: isAdd ? '#e8f5e9' : '#fdecea',
                    color: isAdd ? '#1b5e20' : '#7f0000',
                    border: `1px solid ${isAdd ? '#a5d6a7' : '#ef9a9a'}`,
                }}>
                    <i className={`fa fa-${isAdd ? 'plus' : 'minus'}`} style={{fontSize: 9}}/>
                    {tag}
                </span>
            ))}
        </div>
    );
}

function TagChangeBody({changes}) {
    const hasAdded   = changes.added   && changes.added.length   > 0;
    const hasRemoved = changes.removed && changes.removed.length > 0;
    return (
        <div>
            {hasAdded && (
                <div className="mb-2">
                    <span style={{fontSize: 11, fontWeight: 600, color: '#388e3c', textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                        Added
                    </span>
                    <TagPills tags={changes.added} color="success"/>
                </div>
            )}
            {hasRemoved && (
                <div>
                    <span style={{fontSize: 11, fontWeight: 600, color: '#c62828', textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                        Removed
                    </span>
                    <TagPills tags={changes.removed} color="danger"/>
                </div>
            )}
        </div>
    );
}

function DiffBody({changes}) {
    const oldChanges = changes.old        || {};
    const newChanges = changes.attributes || {};
    const changeKeys = Object.keys(oldChanges).length
        ? Object.keys(oldChanges)
        : Object.keys(newChanges);

    if (changeKeys.length === 0) {
        return <div className="text-muted small">No change details available.</div>;
    }

    return (
        <div>
            {changeKeys.map((changeItem, ci) => (
                <div key={ci} className="mb-3">
                    <h6 className="log-title m-0 pt-0">{changeItem}</h6>
                    {Array.isArray(oldChanges[changeItem]) ? (
                        oldChanges[changeItem].map((nestedItem, ni) => (
                            <div key={ni} className="ps-3 mb-2">
                                {typeof nestedItem === 'object' ? (
                                    <table className="table table-xs table-bordered mb-0">
                                        <tbody>
                                            {Object.keys(nestedItem).map(nestedKey => {
                                                const oldVal = oldChanges[changeItem]?.[ni]?.[nestedKey];
                                                const newVal = newChanges[changeItem]?.[ni]?.[nestedKey];
                                                if (oldVal === newVal) return null;
                                                return (
                                                    <tr key={nestedKey}>
                                                        <td><strong>{nestedKey}</strong></td>
                                                        <td className="text-danger">{oldVal ?? '∅'}</td>
                                                        <td className="text-center">→</td>
                                                        <td className="text-success">{newVal ?? '∅'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="row">
                                        <div className="col text-danger">{nestedItem ?? '∅'}</div>
                                        <div className="col text-success">{newChanges[changeItem]?.[ni] ?? '∅'}</div>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="ps-3 row">
                            <div className="col text-danger">{oldChanges[changeItem] ?? '∅'}</div>
                            <div className="col text-success">{newChanges[changeItem] ?? '∅'}</div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function LogActivity(props) {
    const dispatch = useDispatch();
    const state    = useSelector(({log}) => log.log);
    const {id = null, log} = props;
    const panelRef = useRef(null);

    useEffect(() => {
        if (state.drawer.isOpen) {
            const dataObj = {log: state.drawer.log, subject_id: state.drawer.id};
            dispatch(getLogs(dataObj)).then(res => {
                dispatch(setLogs(res.payload));
            });
        }
    }, [dispatch, state.drawer.isOpen]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (state.drawer.isOpen && panelRef.current && !panelRef.current.contains(event.target)) {
                dispatch(toggleQuickPanel({isOpen: false, id: null, log: null}));
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [state.drawer.isOpen, dispatch]);

    return (
        <div className={`actitity-log ${props.className ?? ''}`} ref={panelRef}>
            <div className="float-right">
                <button className="btn btn-default float-right"
                    onClick={() => dispatch(toggleQuickPanel({isOpen: true, id, log}))}>
                    <i className="fa fa-clock-o fa-2x"/>
                </button>
            </div>

            <div className={`customizer-contain ${state.drawer.isOpen ? 'open' : ''}`}>
                <div className="customizer-header">
                    <i className="icon-close"
                        onClick={() => dispatch(toggleQuickPanel({isOpen: false, id: null, log: null}))}/>
                    <h5>Activity Log</h5>

                    <div style={{height: 1000}}>
                        <ScrollArea horizontal={false} vertical={true}>
                            {state.logs.length === 0 ? (
                                <div className="text-muted text-center p-4" style={{fontSize: 13}}>
                                    No activity recorded yet.
                                </div>
                            ) : (
                                <Accordion defaultActiveKey="0">
                                    {state.logs.map((item, index) => {
                                        const changes  = item?.changes || {};
                                        const isTagLog = changes.added || changes.removed;

                                        return (
                                            <Card key={index} style={{margin: 0}}>
                                                <Card.Header style={{padding: '8px 12px'}}>
                                                    <Row className="align-items-center">
                                                        <Col sm={1} className="pe-0">
                                                            <InitialsAvatar className="log-activity-image" name={item.created_by || '?'}/>
                                                        </Col>
                                                        <Col sm={8}>
                                                            <Label className="form-label mb-0" style={{fontSize: 13}}>
                                                                {isTagLog && (
                                                                    <i className="fa fa-tag mr-1" style={{
                                                                        fontSize: 11,
                                                                        color: changes.added ? '#388e3c' : '#c62828',
                                                                    }}/>
                                                                )}
                                                                {item.description}
                                                            </Label>
                                                            <div className="text-muted" style={{fontSize: 11}}>
                                                                {item.created_by} &bull; {item.date}
                                                            </div>
                                                        </Col>
                                                        <Col sm={3} className="text-end">
                                                            <CustomToggle eventKey={index}/>
                                                        </Col>
                                                    </Row>
                                                </Card.Header>

                                                <Accordion.Collapse eventKey={index}>
                                                    <Card.Body className="log-changes-body">
                                                        {isTagLog
                                                            ? <TagChangeBody changes={changes}/>
                                                            : <DiffBody changes={changes}/>
                                                        }
                                                    </Card.Body>
                                                </Accordion.Collapse>
                                            </Card>
                                        );
                                    })}
                                </Accordion>
                            )}
                        </ScrollArea>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default withReducer([{log: reducer}])(memo(LogActivity));
